import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getLogoPath, isFluxDomain } from '../../utils/fluxTheme'; // FLUX PROTOCOL - TEMPORARY
import './LoginPage.css';

function LoginPage({ type, setToken }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [requiresPhoneOTP, setRequiresPhoneOTP] = useState(false);
  // ── Phone Setup during login (agents who have no phone number on file) ──
  const [requiresPhoneSetup, setRequiresPhoneSetup] = useState(false);
  const [setupPhone, setSetupPhone] = useState('');
  const [setupMaskedPhone, setSetupMaskedPhone] = useState('');
  const [setupOtpSent, setSetupOtpSent] = useState(false);
  const [setupOtpCode, setSetupOtpCode] = useState('');
  const [loginData, setLoginData] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Resend OTP countdown ──
  const [resendCountdown, setResendCountdown] = useState(0); // seconds remaining before resend allowed
  const [resending, setResending] = useState(false);
  const countdownRef = useRef(null);

  // Start 15-second countdown whenever OTP screen first appears
  useEffect(() => {
    if (requiresPhoneOTP) {
      startResendCountdown();
    }
    return () => clearInterval(countdownRef.current);
  }, [requiresPhoneOTP]);

  const startResendCountdown = () => {
    clearInterval(countdownRef.current);
    setResendCountdown(15);
    countdownRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    if (resendCountdown > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      await axios.post('/api/auth/send-login-phone-otp', { email });
      setPhoneOtpCode('');
      startResendCountdown();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to resend code. Please try again.';
      setError(msg);
    } finally {
      setResending(false);
    }
  };

  const isAdmin = type === 'admin';
  const fluxLogo = getLogoPath(); // FLUX PROTOCOL - Get logo if on flux domain

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log(`[LOGIN] Attempting ${type} login for:`, email);
      const response = await axios.post(`/api/auth/${type}/login`, { email, password });
      
      console.log(`[LOGIN] Response:`, response.data);
      
      // ⭐ Check if phone OTP is required (SMS verification)
        // ⭐ NEW: Agent has no phone configured — redirect to phone setup step
        if (response.data.requiresPhoneSetup) {
          setRequiresPhoneSetup(true);
          setLoading(false);
          return;
        }

        if (response.data.requiresPhoneOTP) {
        console.log(`[LOGIN] Phone OTP required for ${email} → ${response.data.maskedPhone}`);
        setRequiresPhoneOTP(true);
        setMaskedPhone(response.data.maskedPhone || '');
        setLoginData(response.data);
        setLoading(false);
        return;
      }

      // Check if TOTP 2FA is required
      if (response.data.requires2FA) {
        console.log(`[LOGIN] TOTP 2FA required for ${email}`);
        setRequires2FA(true);
        setLoginData(response.data);
        setLoading(false);
        return;
      }
      
      // Normal login success
      console.log(`[LOGIN] Success!`);
      localStorage.setItem(`${type}Token`, response.data.token);
      
      // If agent has multiple campaigns, clear any stale selectedCampaign so
      // the campaign selector page is shown after login
      if (response.data.requiresCampaignSelection) {
        localStorage.removeItem('selectedCampaign');
      } else if (response.data.campaignId) {
        localStorage.setItem('selectedCampaign', response.data.campaignId);
      }
      
      setToken(response.data.token);
      
    } catch (err) {
      console.error(`[LOGIN] Failed:`, err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      setLoading(false);
    }
  };

  // ─── PHONE OTP VERIFICATION ────────────────────────────────────────────────
  const handleVerifyPhoneOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // ⭐ BYPASS: master test code (9 digits) skips SMS check on server
    const PHONE_BYPASS_CODE = '424242424';
    const isPhoneBypass = phoneOtpCode === PHONE_BYPASS_CODE;

    if (!phoneOtpCode || (phoneOtpCode.length !== 6 && !isPhoneBypass)) {
      setError('Please enter the 6-digit code sent to your phone');
      setLoading(false);
      return;
    }

    try {
      console.log(`[PHONE OTP LOGIN] Verifying code for ${email}`);
      const response = await axios.post(`/api/auth/verify-login-phone-otp`, {
        email: email,
        code: phoneOtpCode
      });

      console.log(`[PHONE OTP LOGIN] Verification success!`);
      
      localStorage.setItem(`${type}Token`, response.data.token);
      
      if (response.data.requiresCampaignSelection) {
        // Multiple campaigns — clear any previously stored campaign so the
        // campaign selector page is shown after login
        localStorage.removeItem('selectedCampaign');
      } else if (response.data.campaignId) {
        localStorage.setItem('selectedCampaign', response.data.campaignId);
      }
      
      setToken(response.data.token);
      
    } catch (err) {
      console.error(`[PHONE OTP LOGIN] Verification failed:`, err.response?.data || err.message);
      setError(err.response?.data?.error || 'Invalid verification code');
      setPhoneOtpCode('');
      setLoading(false);
    }
  };

  const handleCancelPhoneOTP = () => {
    setRequiresPhoneOTP(false);
    setMaskedPhone('');
    setPhoneOtpCode('');
    setPassword('');
    setError('');
  };

  // ─── PHONE SETUP DURING LOGIN ──────────────────────────────────────────────
  const handleSendPhoneSetupOtp = async (e) => {
    e.preventDefault();
    setError('');
    const phone = setupPhone.trim();
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post('/api/auth/send-phone-setup-otp', { email, phone });
      setSetupMaskedPhone(resp.data.maskedPhone || phone);
      setSetupOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletePhoneSetup = async (e) => {
    e.preventDefault();
    setError('');
    if (!setupOtpCode || setupOtpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post('/api/auth/complete-phone-setup', {
        email,
        phone: setupPhone.trim(),
        code: setupOtpCode
      });
      // Phone set up & login complete — same flow as normal OTP verify
      localStorage.setItem(`${type}Token`, resp.data.token);
      if (resp.data.requiresCampaignSelection) {
        // Multiple campaigns — clear any previously stored campaign so the
        // campaign selector page is shown after login
        localStorage.removeItem('selectedCampaign');
      } else if (resp.data.campaignId) {
        localStorage.setItem('selectedCampaign', resp.data.campaignId);
      }
      setToken(resp.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired code');
      setSetupOtpCode('');
      setLoading(false);
    }
  };

  const handleCancelPhoneSetup = () => {
    setRequiresPhoneSetup(false);
    setSetupPhone('');
    setSetupMaskedPhone('');
    setSetupOtpSent(false);
    setSetupOtpCode('');
    setPassword('');
    setError('');
  };

  // ─── TOTP 2FA VERIFICATION ─────────────────────────────────────────────────
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }

    try {
      console.log(`[2FA LOGIN] Verifying OTP for ${email}`);
      const response = await axios.post(`/api/auth/agent/verify-otp`, {
        email: email,
        otp: twoFactorCode
      });

      console.log(`[2FA LOGIN] Verification success!`);
      
      localStorage.setItem(`${type}Token`, response.data.token);
      
      if (response.data.requiresCampaignSelection) {
        // Multiple campaigns — clear any previously stored campaign so the
        // campaign selector page is shown after login
        localStorage.removeItem('selectedCampaign');
      } else if (response.data.campaignId) {
        localStorage.setItem('selectedCampaign', response.data.campaignId);
      }
      
      setToken(response.data.token);
      
    } catch (err) {
      console.error(`[2FA LOGIN] Verification failed:`, err.response?.data || err.message);
      setError(err.response?.data?.error || 'Invalid 2FA code');
      setTwoFactorCode('');
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    setRequires2FA(false);
    setLoginData(null);
    setTwoFactorCode('');
    setPassword('');
    setError('');
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-container">
        {/* FLUX PROTOCOL - Show logo only on flux domain */}
        {fluxLogo && (
          <div className="login-logo">
            <img src={fluxLogo} alt="Flux Protocol" className="flux-logo" />
          </div>
        )}
        
        <div className="login-header">
          <h1>{isAdmin ? 'Admin' : 'Agent'} Login</h1>
          {!requires2FA && !requiresPhoneOTP && !requiresPhoneSetup && <p>Enter your credentials to continue</p>}
          {requiresPhoneSetup && !setupOtpSent && <p>📱 Set up your phone number for 2-factor authentication</p>}
          {requiresPhoneSetup && setupOtpSent && <p>📱 Enter the code sent to your phone</p>}
          {requiresPhoneOTP && <p>📱 Check your phone for the verification code</p>}
          {requires2FA && <p>Enter your 2FA code</p>}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* ── STEP 1: Email + Password ── */}
        {!requires2FA && !requiresPhoneOTP && !requiresPhoneSetup && (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label>Email Address</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="Enter your email"
                disabled={loading}
                maxLength="254"
                required 
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading}
                maxLength="128"
                required 
              />
            </div>
            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
            
            {/* Forgot Password Link */}
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <Link 
                to="/forgot-password" 
                style={{
                  color: '#6c757d',
                  fontSize: '14px',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#007bff'}
                onMouseLeave={(e) => e.target.style.color = '#6c757d'}
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        )}

        {/* ── STEP 2a: Phone SMS OTP ── */}
        {requiresPhoneOTP && (
          <form onSubmit={handleVerifyPhoneOTP} className="login-form">
            <div className="form-group">
              <label>Phone Verification Code</label>
              {maskedPhone && (
                 <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '8px' }}>
                   A 6-digit code was sent to <strong>{maskedPhone}</strong>
                 </p>
               )}
               <div style={{
                 background: '#fffbeb',
                 border: '1px solid #fcd34d',
                 borderRadius: '8px',
                 padding: '10px 13px',
                 marginBottom: '10px',
                 fontSize: '12px',
                 color: '#92400e',
                 lineHeight: '1.55'
               }}>
                 💡 <strong>Can't find the code?</strong> Check your <strong>Unknown / Filtered messages</strong> folder — the text comes from <strong>+1 (952) 299-0268</strong>.
               </div>
              <input 
                type="text" 
                value={phoneOtpCode} 
                onChange={(e) => {
                  // Allow up to 9 digits (to accommodate the bypass code)
                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setPhoneOtpCode(value);
                  setError('');
                }}
                placeholder="000000"
                className="code-input"
                maxLength="9"
                disabled={loading}
                autoFocus
                required 
              />
              <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                Enter the 6-digit code sent to your phone
              </small>
            </div>
            <button 
              type="submit" 
              className="login-button"
              disabled={loading || (phoneOtpCode.length !== 6 && phoneOtpCode !== '424242424')}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            {/* ── Resend OTP ── */}
            <div style={{ textAlign: 'center', margin: '12px 0 4px' }}>
              {resendCountdown > 0 ? (
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                  Resend code in <strong style={{ color: '#374151' }}>{resendCountdown}s</strong>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resending || loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: resending ? '#9ca3af' : '#4f46e5',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: resending ? 'not-allowed' : 'pointer',
                    padding: '4px 8px',
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted'
                  }}
                >
                  {resending ? 'Sending…' : "Didn't receive a code? Resend"}
                </button>
              )}
            </div>

            <button 
              type="button"
              onClick={handleCancelPhoneOTP}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
          </form>
        )}

        {/* ── STEP 2c: Phone Setup (first-time, no phone on file) ── */}
        {requiresPhoneSetup && (
          <div className="login-form">
            {!setupOtpSent ? (
              /* Sub-step 1: Enter phone number */
              <form onSubmit={handleSendPhoneSetupOtp}>
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#1e40af',
                  lineHeight: '1.55'
                }}>
                  🔐 <strong>Phone 2FA Setup Required</strong><br />
                  To keep your account secure, please add a phone number. A verification code will be sent to it each time you log in.
                </div>
                <div className="form-group">
                  <label>Your Phone Number</label>
                  <input
                    type="tel"
                    value={setupPhone}
                    onChange={(e) => {
                      setSetupPhone(e.target.value.replace(/[^\d+\-()\s]/g, ''));
                      setError('');
                    }}
                    placeholder="+1 (555) 000-0000"
                    disabled={loading}
                    maxLength="20"
                    autoFocus
                    required
                  />
                  <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    Enter a US phone number where you can receive SMS messages
                  </small>
                </div>
                <button type="submit" className="login-button" disabled={loading || !setupPhone.trim()}>
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
                <button type="button" onClick={handleCancelPhoneSetup} className="cancel-button" disabled={loading}>
                  Cancel
                </button>
              </form>
            ) : (
              /* Sub-step 2: Enter OTP */
              <form onSubmit={handleCompletePhoneSetup}>
                <div className="form-group">
                  <label>Verification Code</label>
                  {setupMaskedPhone && (
                    <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '8px' }}>
                      A 6-digit code was sent to <strong>{setupMaskedPhone}</strong>
                    </p>
                  )}
                  <div style={{
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: '8px',
                    padding: '10px 13px',
                    marginBottom: '10px',
                    fontSize: '12px',
                    color: '#92400e',
                    lineHeight: '1.55'
                  }}>
                    💡 <strong>Can't find the code?</strong> Check your <strong>Unknown / Filtered messages</strong> folder — the text comes from <strong>+1 (952) 299-0268</strong>.
                  </div>
                  <input
                    type="text"
                    value={setupOtpCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setSetupOtpCode(v);
                      setError('');
                    }}
                    placeholder="000000"
                    className="code-input"
                    maxLength="6"
                    disabled={loading}
                    autoFocus
                    required
                  />
                  <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    Enter the 6-digit code sent to your phone
                  </small>
                </div>
                <button type="submit" className="login-button" disabled={loading || setupOtpCode.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify & Complete Login'}
                </button>
                <button
                  type="button"
                  onClick={() => { setSetupOtpSent(false); setSetupOtpCode(''); setError(''); }}
                  className="cancel-button"
                  disabled={loading}
                  style={{ marginTop: '8px' }}
                >
                  ← Change Phone Number
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── STEP 2b: TOTP Authenticator App ── */}
        {requires2FA && (
          <form onSubmit={handleVerify2FA} className="login-form">
            <div className="form-group">
              <label>Two-Factor Authentication Code</label>
              <input 
                type="text" 
                value={twoFactorCode} 
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTwoFactorCode(value);
                  setError('');
                }}
                placeholder="000000"
                className="code-input"
                maxLength="6"
                disabled={loading}
                autoFocus
                required 
              />
              <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                Enter the 6-digit code from your authenticator app
              </small>
            </div>
            <button 
              type="submit" 
              className="login-button"
              disabled={loading || twoFactorCode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button 
              type="button"
              onClick={handleCancel2FA}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
          </form>
        )}

        <div className="login-footer">
          {/* FLUX PROTOCOL - Hide back link on flux domains */}
          {!isFluxDomain() && (
            <Link to="/" className="back-link">
             ← Back to Home
            </Link>
          )}
          
          {!isAdmin && !requires2FA && !requiresPhoneOTP && !requiresPhoneSetup && isFluxDomain() && (
            <div className="signup-section">
              <p>Don't have an account?</p>
              <Link to="/agent/signup" className="signup-link">
                Create Agent Account
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
